import React, { useEffect, useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MIN_SIZE = 2, MAX_SIZE = 8;
const MODES = [
  { key: 'build', label: 'Build & Traverse' },
  { key: 'reverse', label: 'Reverse the List' },
  { key: 'cycle', label: 'Detect a Cycle' },
  { key: 'merge', label: 'Merge Two Sorted Lists' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Node chain: boxes connected by arrows, tags for named pointers ── */
const NodeChain = ({ nodes, getNodeState, terminator }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0, paddingTop: 28, paddingBottom: 20 }}>
    {nodes.map((v, i) => {
      const st = getNodeState ? getNodeState(i) : {};
      return (
        <React.Fragment key={i}>
          <div style={{ position: 'relative', textAlign: 'center' }}>
            {st.tags && st.tags.map((t, ti) => (
              <span key={ti} style={{
                position: 'absolute', top: -24 - ti * 15, left: '50%', transform: 'translateX(-50%)',
                fontSize: 9.5, fontWeight: 800, color: t.color, whiteSpace: 'nowrap',
              }}>{t.text}</span>
            ))}
            <div style={{
              width: 50, height: 50, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 800, fontFamily: "'Inter',sans-serif",
              background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
              border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
            }}>{v}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>{i}</div>
          </div>
          {i < nodes.length - 1 && <span style={{ padding: '0 5px', color: st.arrowColor || 'var(--muted)', fontSize: 17, flexShrink: 0 }}>{st.arrow || '→'}</span>}
        </React.Fragment>
      );
    })}
    {terminator !== null && (
      <>
        <span style={{ padding: '0 5px', color: terminator?.color || 'var(--muted)', fontSize: 17, flexShrink: 0 }}>{terminator?.arrow || '→'}</span>
        <div style={{
          padding: '0 12px', height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 10, border: `1.5px dashed ${terminator?.color || 'var(--border)'}`, color: terminator?.color || 'var(--muted)',
          fontSize: 12, fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'nowrap',
        }}>{terminator?.label || 'null'}</div>
      </>
    )}
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext, playing, onPlay }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    {onPlay && <button onClick={onPlay} style={smallBtn(playing ? RED : GREEN)}>{playing ? 'Pause' : 'Auto Play'}</button>}
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);

/* ─── Build & Traverse ───────────────────────────────────────────── */
const BuildMode = () => {
  const [list, setList] = useState([10, 20, 30]);
  const [value, setValue] = useState('');
  const [traverseIdx, setTraverseIdx] = useState(-1);
  const [lastAction, setLastAction] = useState({ action: null });

  const insertHead = () => {
    if (value.trim() === '' || isNaN(Number(value))) return;
    setList(l => [Number(value), ...l]);
    setLastAction({ action: 'insertHead', value: Number(value) });
    setValue(''); setTraverseIdx(-1);
  };
  const insertTail = () => {
    if (value.trim() === '' || isNaN(Number(value))) return;
    setList(l => [...l, Number(value)]);
    setLastAction({ action: 'insertTail', value: Number(value) });
    setValue(''); setTraverseIdx(-1);
  };
  const deleteHead = () => {
    if (list.length === 0) return;
    setList(l => l.slice(1));
    setLastAction({ action: 'delete' });
    setTraverseIdx(-1);
  };

  const getNodeState = i => {
    if (i === traverseIdx) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags: [{ text: 'curr', color: V2 }] };
    if (i === 0) return { tags: [{ text: 'head', color: TEAL }] };
    if (i === list.length - 1) return { tags: [{ text: 'tail', color: TEAL }] };
    return {};
  };

  const code = {
    lines: [
      'class ListNode { int val; ListNode next; }',
      'ListNode newNode = new ListNode(value);',
      'newNode.next = head; head = newNode;   // insert at head',
      '',
      'ListNode curr = head;',
      'while (curr != null) {',
      '    System.out.println(curr.val);',
      '    curr = curr.next;',
      '}',
    ],
    active: !lastAction.action ? -1 : lastAction.action === 'insertHead' ? 2 : lastAction.action === 'insertTail' ? 2 : traverseIdx >= 0 ? 7 : -1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <NodeChain nodes={list} getNodeState={getNodeState} terminator={{}} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={value} onChange={e => setValue(e.target.value)} style={smallInput} placeholder="value" />
            <button onClick={insertHead} style={smallBtn(V1)}>Insert at Head</button>
            <button onClick={insertTail} style={smallBtn(TEAL)}>Insert at Tail</button>
            <button onClick={deleteHead} style={smallBtn(RED, list.length === 0)} disabled={list.length === 0}>Delete Head</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => setTraverseIdx(-1)} style={ghostBtn(false)}>Reset curr</button>
            <button onClick={() => setTraverseIdx(i => Math.min(list.length - 1, i + 1))} disabled={traverseIdx >= list.length - 1} style={smallBtn(V1, traverseIdx >= list.length - 1)}>Step curr = curr.next</button>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {traverseIdx === -1 ? 'curr starts at head. Click "Step" to walk it forward one node at a time.' : `curr is now at node ${traverseIdx} (value ${list[traverseIdx]}).`}
          </p>
        </div>
      </div>
      <CodePanel title="Java — Insert & Traverse" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Reverse the List ───────────────────────────────────────────── */
const ReverseMode = () => {
  const [sizeInput, setSizeInput] = useState('5');
  const [list, setList] = useState([1, 2, 3, 4, 5]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);

  const createList = () => {
    const n = Math.min(MAX_SIZE, Math.max(MIN_SIZE, parseInt(sizeInput, 10) || MIN_SIZE));
    setList(Array.from({ length: n }, (_, i) => (i + 1) * 10));
    setSteps(null); setStep(0); setPlaying(false);
  };
  const startReverse = () => {
    const n = list.length;
    const built = [];
    for (let i = 0; i <= n; i++) {
      built.push({ prevIdx: i - 1, currIdx: i < n ? i : null, nextIdx: i < n - 1 ? i + 1 : null });
    }
    setSteps(built); setStep(0); setPlaying(false);
  };

  useEffect(() => {
    if (!playing || !steps) return;
    if (step >= steps.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setStep(s => s + 1), 950);
    return () => clearTimeout(t);
  }, [playing, step, steps]);

  const cur = steps ? steps[step] : null;
  const getNodeState = i => {
    if (!cur) return {};
    const reversed = i < step; // nodes with index < current step are already flipped
    const tags = [];
    if (i === cur.prevIdx) tags.push({ text: 'prev', color: TEAL });
    if (i === cur.currIdx) tags.push({ text: 'curr', color: V2 });
    if (i === cur.nextIdx) tags.push({ text: 'next', color: GREEN });
    if (i === cur.currIdx) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags, arrow: reversed ? '←' : '→', arrowColor: reversed ? GREEN : 'var(--muted)' };
    return { tags, arrow: reversed ? '←' : '→', arrowColor: reversed ? GREEN : 'var(--muted)', bg: reversed ? 'var(--bg2)' : undefined, border: reversed ? GREEN : undefined };
  };

  const note = !cur ? 'Build a list, then Reverse.' :
    cur.currIdx === null ? 'curr is now null — the loop ends. Every node\'s arrow now points backward: the list is fully reversed.' :
    `curr is node ${cur.currIdx}. Rewire: curr.next = prev (flip this node's arrow backward), then prev = curr, then curr = next.`;

  const code = {
    lines: [
      'ListNode prev = null, curr = head;',
      'while (curr != null) {',
      '    ListNode next = curr.next;   // save it before overwriting',
      '    curr.next = prev;             // reverse this node\'s pointer',
      '    prev = curr;',
      '    curr = next;',
      '}',
      'head = prev;   // prev is the new head',
    ],
    active: !cur ? -1 : cur.currIdx === null ? 7 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <NodeChain nodes={list} getNodeState={getNodeState} terminator={cur && cur.currIdx === null ? null : {}} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 60 }}>Size</span>
            <input value={sizeInput} onChange={e => setSizeInput(e.target.value)} style={smallInput} placeholder="5" />
            <button onClick={createList} style={smallBtn(V1)}>New List</button>
            <button onClick={startReverse} style={smallBtn(GREEN)}>Reverse</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} playing={playing} onPlay={() => setPlaying(p => !p)} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Iterative Reversal" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Detect a Cycle ─────────────────────────────────────────────── */
const CycleMode = () => {
  const [sizeInput, setSizeInput] = useState('6');
  const [list, setList] = useState([1, 2, 3, 4, 5, 6]);
  const [cycleIdx, setCycleIdx] = useState('2');
  const [hasCycle, setHasCycle] = useState(true);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);

  const createList = () => {
    const n = Math.min(MAX_SIZE, Math.max(MIN_SIZE, parseInt(sizeInput, 10) || MIN_SIZE));
    setList(Array.from({ length: n }, (_, i) => (i + 1) * 10));
    setSteps(null); setStep(0); setPlaying(false);
  };

  const next = i => {
    const n = list.length;
    if (i < n - 1) return i + 1;
    return hasCycle ? Math.min(n - 1, Math.max(0, parseInt(cycleIdx, 10) || 0)) : null;
  };

  const startDetect = () => {
    const built = [];
    let slow = 0, fast = 0;
    const maxSteps = list.length * 2 + 3;
    for (let s = 0; s < maxSteps; s++) {
      const slowNext = next(slow);
      const fastNext1 = next(fast);
      const fastNext = fastNext1 === null ? null : next(fastNext1);
      if (slowNext === null || fastNext === null) { built.push({ done: true, cycle: false, slow, fast }); break; }
      slow = slowNext; fast = fastNext;
      built.push({ slow, fast });
      if (slow === fast) { built.push({ done: true, cycle: true, slow, fast }); break; }
    }
    if (built.length === 0 || !built[built.length - 1].done) built.push({ done: true, cycle: hasCycle, slow, fast });
    setSteps(built); setStep(0); setPlaying(false);
  };

  useEffect(() => {
    if (!playing || !steps) return;
    if (step >= steps.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setStep(s => s + 1), 900);
    return () => clearTimeout(t);
  }, [playing, step, steps]);

  const cur = steps ? steps[step] : null;
  const getNodeState = i => {
    if (!cur) return {};
    const tags = [];
    if (i === cur.slow) tags.push({ text: 'slow', color: V2 });
    if (i === cur.fast) tags.push({ text: 'fast', color: TEAL });
    if (tags.length === 2) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', tags: [{ text: 'slow = fast!', color: GREEN }] };
    if (i === cur.slow) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags };
    if (i === cur.fast) return { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tags };
    return {};
  };

  const note = !cur ? 'Build a list, choose whether it cycles back, then Detect.' :
    cur.done ? (cur.cycle ? 'slow and fast landed on the same node — a cycle is confirmed.' : 'fast reached the end (null) — no cycle exists.') :
    'slow moves one step, fast moves two — if there\'s a cycle, fast will eventually lap slow.';

  const code = {
    lines: [
      'ListNode slow = head, fast = head;',
      'while (fast != null && fast.next != null) {',
      '    slow = slow.next;',
      '    fast = fast.next.next;',
      '    if (slow == fast) return true;   // cycle found',
      '}',
      'return false;   // fast reached the end',
    ],
    active: !cur ? -1 : cur.done ? (cur.cycle ? 4 : 6) : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <NodeChain nodes={list} getNodeState={getNodeState} terminator={hasCycle ? { color: RED, arrow: '↩', label: `back to node ${Math.min(list.length - 1, Math.max(0, parseInt(cycleIdx, 10) || 0))}` } : {}} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 60 }}>Size</span>
            <input value={sizeInput} onChange={e => setSizeInput(e.target.value)} style={smallInput} placeholder="6" />
            <button onClick={createList} style={smallBtn(V1)}>New List</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--muted)', fontWeight: 700 }}>
              <input type="checkbox" checked={hasCycle} onChange={e => { setHasCycle(e.target.checked); setSteps(null); }} /> Create a cycle back to index
            </label>
            <input value={cycleIdx} onChange={e => { setCycleIdx(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 60 }} disabled={!hasCycle} />
            <button onClick={startDetect} style={smallBtn(GREEN)}>Detect</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} playing={playing} onPlay={() => setPlaying(p => !p)} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Floyd's Cycle Detection" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Merge Two Sorted Lists ─────────────────────────────────────── */
const MergeMode = () => {
  const [aInput, setAInput] = useState('1, 3, 5');
  const [bInput, setBInput] = useState('2, 4, 6');
  const [a, setA] = useState([1, 3, 5]);
  const [b, setB] = useState([2, 4, 6]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startMerge = () => {
    const parsedA = aInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)).sort((x, y) => x - y);
    const parsedB = bInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)).sort((x, y) => x - y);
    if (parsedA.length === 0 || parsedB.length === 0) return;
    const built = [];
    let i = 0, j = 0; const result = [];
    while (i < parsedA.length && j < parsedB.length) {
      if (parsedA[i] <= parsedB[j]) { result.push(parsedA[i]); built.push({ i, j, picked: 'a', result: [...result] }); i++; }
      else { result.push(parsedB[j]); built.push({ i, j, picked: 'b', result: [...result] }); j++; }
    }
    while (i < parsedA.length) { result.push(parsedA[i]); built.push({ i, j, picked: 'a', result: [...result] }); i++; }
    while (j < parsedB.length) { result.push(parsedB[j]); built.push({ i, j, picked: 'b', result: [...result] }); j++; }
    built.push({ done: true, result: [...result] });
    setA(parsedA); setB(parsedB);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const getStateA = idx => (cur && !cur.done && idx === cur.i) ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags: [{ text: 'i', color: V2 }] } : {};
  const getStateB = idx => (cur && !cur.done && idx === cur.j) ? { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tags: [{ text: 'j', color: TEAL }] } : {};
  const note = !cur ? 'Enter two lists (each will be sorted), then Merge.' :
    cur.done ? `Both lists are exhausted — merged result: [${cur.result.join(', ')}].` :
    cur.picked === 'a' ? `a[${cur.i}] ≤ b[${cur.j}] — append a[${cur.i}] to the result, advance i.` :
    `b[${cur.j}] < a[${cur.i}] — append b[${cur.j}] to the result, advance j.`;

  const code = {
    lines: [
      'ListNode dummy = new ListNode(0), tail = dummy;',
      'while (a != null && b != null) {',
      '    if (a.val <= b.val) { tail.next = a; a = a.next; }',
      '    else { tail.next = b; b = b.next; }',
      '    tail = tail.next;',
      '}',
      'tail.next = (a != null) ? a : b;   // attach whatever remains',
      'return dummy.next;',
    ],
    active: !cur ? -1 : cur.done ? 6 : cur.picked === 'a' ? 2 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>List A</div>
        <NodeChain nodes={a} getNodeState={getStateA} terminator={{}} />
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>List B</div>
        <NodeChain nodes={b} getNodeState={getStateB} terminator={{}} />
        {cur && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: GREEN, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: -6 }}>Merged Result</div>
            <NodeChain nodes={cur.result.length ? cur.result : ['·']} terminator={cur.done ? {} : null} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>List A</span>
            <input value={aInput} onChange={e => { setAInput(e.target.value); setSteps(null); }} style={wideInput} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>List B</span>
            <input value={bInput} onChange={e => { setBInput(e.target.value); setSteps(null); }} style={wideInput} />
            <button onClick={startMerge} style={smallBtn(V1)}>Merge</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Merge Two Sorted Lists" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const a = [];
  const b = [];
  let av = Math.floor(Math.random() * 3) + 1, bv = Math.floor(Math.random() * 3) + 1;
  for (let k = 0; k < 4; k++) { a.push(av); av += Math.floor(Math.random() * 4) + 1; }
  for (let k = 0; k < 4; k++) { b.push(bv); bv += Math.floor(Math.random() * 4) + 1; }
  const i = Math.floor(Math.random() * a.length);
  const j = Math.floor(Math.random() * b.length);
  const correct = a[i] <= b[j] ? 'a' : 'b';
  return { a, b, i, j, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const getStateA = idx => idx === scenario.i ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tags: [{ text: 'i', color: V2 }] } : {};
  const getStateB = idx => idx === scenario.j ? { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tags: [{ text: 'j', color: TEAL }] } : {};

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>List A</div>
      <NodeChain nodes={scenario.a} getNodeState={getStateA} terminator={{}} />
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>List B</div>
      <NodeChain nodes={scenario.b} getNodeState={getStateB} terminator={{}} />
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '10px 0 14px' }}>
        While merging, i points to a[{scenario.i}]={scenario.a[scenario.i]} and j points to b[{scenario.j}]={scenario.b[scenario.j]}. Which one gets appended to the merged result next?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {[{ key: 'a', label: `Take from List A (${scenario.a[scenario.i]})` }, { key: 'b', label: `Take from List B (${scenario.b[scenario.j]})` }].map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const LinkedListPlayground = () => {
  const [mode, setMode] = useState('build');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'build' && <BuildMode />}
      {mode === 'reverse' && <ReverseMode />}
      {mode === 'cycle' && <CycleMode />}
      {mode === 'merge' && <MergeMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default LinkedListPlayground;
